import * as moment from 'moment';
import { ModelStatus } from '../../../shared/dtos/model-status';
import { SelectionItem } from '../../../shared/dtos/selection-item';
import { FrequencyLimitUnitLabels } from '../dtos/frequency-limit-unit.enum';
import { GeogatingMode, GeogatingType } from '../dtos/geogating.enum';
import { SavedServiceLevelConfiguration, UnsavedServiceLevelConfiguration } from '../dtos/service-level-config.dto';

export class UnsavedServiceLevelConfigurationModel {
    public _geogatingMode: string;
    public _geogatingType: string;
    protected _configuration: UnsavedServiceLevelConfiguration;
    public _productCode: string;

    public readonly organizationId: number;
    public serviceLevelId: number;
    public serviceLevelName: string;
    public readonly marketSectorId: number;
    public readonly durations: SelectionItem[] = [];
    public readonly coverageLevels: SelectionItem[];

    public get productCode(): string {
        return this._productCode;
    }

    public set productCode(value: string) {
        this._productCode = value;
        this.modelStatus = ModelStatus.NEW;
    }

    public get geogatingMode(): string {
        return this._geogatingMode;
    }

    public set geogatingMode(value: string) {
        this._geogatingMode = value;
        this.modelStatus = ModelStatus.NEW;
    }

    public get geogatingType(): string {
        return this._geogatingType;
    }

    public set geogatingType(value: string) {
        this._geogatingType = value;
        this.modelStatus = ModelStatus.NEW;
    }

    public constructor(configuration: UnsavedServiceLevelConfiguration) {
        this._configuration = configuration;
        this.organizationId = this._configuration.organizationId;
        this.serviceLevelId = this._configuration.serviceLevelId;
        this.marketSectorId = this._configuration.marketSectorId;
        this._productCode = this._configuration.productCode;
        this.durations = this._configuration.durations ?? [];
        this.coverageLevels = this._configuration.coverageLevels ?? [];
        this._geogatingMode = this._configuration.geogatingMode;
        this._geogatingType = this._configuration.geogatingType;
        this.modelStatus = this._configuration.modelStatus;
        this.serviceLevelName = this._configuration.serviceLevelName;
    }

    public getDurationsAsString(): string {
        return this.durations.sort((a, b) => {
            const aDuration = parseInt(a.name);
            const aUnit = a.value.toLowerCase() as moment.unitOfTime.DurationConstructor;
            const aDate = moment().startOf('day').add(1, aUnit);
            const bDuration = parseInt(b.name);
            const bUnit = b.value.toLowerCase() as moment.unitOfTime.DurationConstructor;
            const bDate = moment().startOf('day').add(1, bUnit);

            const diff = aDate.diff(bDate);

            if (diff === 0) {
                return aDuration - bDuration;
            }

            return diff;
        }).map(i => i.name + FrequencyLimitUnitLabels[i.value]).join(', ');
    }

    public getCoverageLevelsAsString(): string {
        return this.coverageLevels.map(i => i.name).join(', ');
    }

    public modelStatus: ModelStatus;

    public getConfigurationData(): UnsavedServiceLevelConfiguration {
        return {
            coverageLevels: this.coverageLevels,
            durations: this.durations,
            marketSectorId: this.marketSectorId,
            modelStatus: this.modelStatus,
            organizationId: this.organizationId,
            productCode: this.productCode,
            serviceLevelName: this.serviceLevelName,
            serviceLevelId: this.serviceLevelId,
            geogatingMode: this.geogatingMode,
            geogatingType: this.geogatingType
        };
    }

    public static getDefault(): UnsavedServiceLevelConfigurationModel {
        return new UnsavedServiceLevelConfigurationModel({
            coverageLevels: [],
            durations: [],
            geogatingMode: GeogatingMode.UNGEOGATED,
            geogatingType: GeogatingType.UNGEOGATED,
            marketSectorId: 0,
            modelStatus: ModelStatus.NEW,
            organizationId: 0,
            productCode: '',
            serviceLevelName: '',
            serviceLevelId: 0
        });
    }
}

export class SavedServiceLevelConfigurationModel extends UnsavedServiceLevelConfigurationModel {
    protected _configuration: SavedServiceLevelConfiguration;
    public accessCodeId: number;
    public readonly serviceLevelConfigId: number;
    public productId: number;
    public readonly accessCodeName: string;
    public readonly marketSectorName: string;

    public get productCode(): string {
        return this._productCode;
    }

    public set productCode(value: string) {
        this._productCode = value;
        this.productId = 0; // to indicate that product code has changed and let backend handle the id
        this.modelStatus = ModelStatus.UPDATE;
    }

    public set geogatingMode(value: string) {
        this._geogatingMode = value;
        this.modelStatus = ModelStatus.UPDATE;
    }

    public get geogatingMode(): string {
        return this._geogatingMode;
    }

    public set geogatingType(value: string) {
        this._geogatingType = value;
        this.modelStatus = ModelStatus.UPDATE;
    }

    public get geogatingType(): string {
        return this._geogatingType;
    }

    public constructor(configuration: SavedServiceLevelConfiguration) {
        super(configuration);
        this._configuration = configuration;
        this.serviceLevelConfigId = this._configuration.serviceLevelConfigId;
        this.productId = this._configuration.productId;
        this.accessCodeName = this._configuration.accessCodeName;
        this.marketSectorName = this._configuration.marketSectorName;
        this._productCode = this._configuration.productCode;
        this.accessCodeName = this._configuration.accessCodeName;
        this.marketSectorName = this._configuration.marketSectorName;
        this.serviceLevelConfigId = this._configuration.serviceLevelConfigId;
        this.accessCodeId = this._configuration.accessCodeId;
    }

    public clone(): SavedServiceLevelConfigurationModel {
        return new SavedServiceLevelConfigurationModel({
            accessCodeId: this.accessCodeId,
            coverageLevels: [...this.coverageLevels],
            durations: [...this.durations],
            marketSectorId: this.marketSectorId,
            modelStatus: this.modelStatus,
            organizationId: this.organizationId,
            productCode: this.productCode,
            serviceLevelId: this.serviceLevelId,
            accessCodeName: this.accessCodeName,
            marketSectorName: this.marketSectorName,
            productId: this.productId,
            serviceLevelConfigId: this.serviceLevelConfigId,
            serviceLevelName: this.serviceLevelName,
            geogatingMode: this.geogatingMode,
            geogatingType: this.geogatingType
        });
    }

    public getConfigurationData(): SavedServiceLevelConfiguration {
        return {
            accessCodeId: this.accessCodeId,
            coverageLevels: this.coverageLevels,
            durations: this.durations,
            marketSectorId: this.marketSectorId,
            modelStatus: this.modelStatus,
            organizationId: this.organizationId,
            productCode: this.productCode,
            serviceLevelId: this.serviceLevelId,
            accessCodeName: this.accessCodeName,
            marketSectorName: this.marketSectorName,
            productId: this.productId,
            serviceLevelConfigId: this.serviceLevelConfigId,
            serviceLevelName: this.serviceLevelName,
            geogatingMode: this.geogatingMode,
            geogatingType: this.geogatingType
        };
    }
}
